import React, { useState, useEffect } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { chatbotUrl } from '../../util/config';

const MOCK_PAIRS = [
    { id: 'qa_001', status: 'pending', confidence: 0.91, question: 'What is the interest rate for home loans?', answer: 'The current interest rate for home loans starts at 8.5% per annum for floating rate loans and 9.0% for fixed rate loans.', source_session: 's_abc123', channel: 'whatsapp', tag: 'loan_faq', extracted_at: new Date(Date.now() - 30 * 60 * 1000).toISOString() },
    { id: 'qa_002', status: 'pending', confidence: 0.87, question: 'How long does it take to process a loan application?', answer: 'Loan applications are typically processed within 7 to 10 working days after all required documents are submitted.', source_session: 's_def456', channel: 'whatsapp', tag: 'loan_faq', extracted_at: new Date(Date.now() - 60 * 60 * 1000).toISOString() },
    { id: 'qa_003', status: 'pending', confidence: 0.74, question: 'Can I prepay my loan?', answer: 'Yes, you can prepay your loan partially or fully. For floating rate loans there is no prepayment penalty.', source_session: 's_ghi789', channel: 'whatsapp', tag: 'loan_faq', extracted_at: new Date(Date.now() - 90 * 60 * 1000).toISOString() },
    { id: 'qa_004', status: 'approved', confidence: 0.95, question: 'What documents are required for a home loan?', answer: 'Required documents include: identity proof (Aadhaar/PAN), address proof, income proof (salary slips/ITR), bank statements for 6 months, and property documents.', source_session: 's_jkl012', channel: 'whatsapp', tag: 'loan_faq', extracted_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString() },
    { id: 'qa_005', status: 'rejected', confidence: 0.62, question: 'My account number', answer: 'Your account number is the 12-digit number on your passbook.', source_session: 's_mno345', channel: 'whatsapp', tag: 'account_support', extracted_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString() },
];

const STATUS_STYLE = {
    pending:  { color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: 'bi-clock'         },
    approved: { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: 'bi-check-circle'  },
    rejected: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: 'bi-x-circle'      },
};

const PIPELINE_STEPS = [
    { icon: 'bi-chat-dots',    label: 'Session ends',        color: '#1565c0', bg: '#eff6ff' },
    { icon: 'bi-cpu',          label: 'Celery extracts Q&A', color: '#7c3aed', bg: '#f5f3ff' },
    { icon: 'bi-funnel',       label: 'Confidence filter',   color: '#d97706', bg: '#fffbeb' },
    { icon: 'bi-clock',        label: 'Review queue',        color: '#0891b2', bg: '#ecfeff' },
    { icon: 'bi-check-circle', label: 'Approved',            color: '#16a34a', bg: '#f0fdf4' },
    { icon: 'bi-lightning',    label: 'Qdrant upsert',       color: '#1565c0', bg: '#eff6ff' },
];

function ConfidenceBar({ value }) {
    const pct   = Math.round(value * 100);
    const color = pct >= 85 ? '#16a34a' : pct >= 70 ? '#d97706' : '#dc2626';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.4s' }}/>
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 34 }}>{pct}%</span>
        </div>
    );
}

function QAPairCard({ pair, onApprove, onReject }) {
    const [expanded, setExpanded] = useState(false);
    const [loading,  setLoading]  = useState(false);
    const ss        = STATUS_STYLE[pair.status] || STATUS_STYLE.pending;
    const isPending = pair.status === 'pending';

    const handle = async (action) => {
        setLoading(true);
        try { await (action === 'approve' ? onApprove(pair.id) : onReject(pair.id)); }
        finally { setLoading(false); }
    };

    return (
        <div style={{ background: '#fff', border: `1px solid ${isPending ? '#e5e7eb' : ss.border}`, borderRadius: 14, overflow: 'hidden', opacity: !isPending ? 0.75 : 1, boxShadow: isPending ? '0 1px 6px rgba(0,0,0,0.06)' : 'none', transition: 'all 0.2s' }}>
            <div style={{ padding: '12px 16px', background: isPending ? '#fff' : ss.bg, borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}>
                            <i className={`bi ${ss.icon}`} style={{ fontSize: 10 }}></i> {pair.status}
                        </span>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: '#eff6ff', color: '#1565c0', fontFamily: 'monospace', fontWeight: 600 }}>{pair.tag}</span>
                        <span style={{ fontSize: 10, color: '#9ca3af' }}>
                            <i className="bi bi-whatsapp" style={{ marginRight: 3 }}></i>
                            {pair.channel} · {new Date(pair.extracted_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                    <ConfidenceBar value={pair.confidence}/>
                </div>
                <button onClick={() => setExpanded(p => !p)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
                    <i className="bi bi-eye" style={{ fontSize: 15 }}></i>
                </button>
            </div>

            <div style={{ padding: '14px 16px' }}>
                <div style={{ marginBottom: 10 }}>
                    <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Question</p>
                    <p style={{ fontSize: 13, color: '#111827', fontWeight: 500, lineHeight: 1.5, margin: 0 }}>{pair.question}</p>
                </div>
                <div>
                    <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Answer</p>
                    <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6, margin: 0 }}>{pair.answer}</p>
                </div>
                {expanded && (
                    <div style={{ marginTop: 12, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 11, fontFamily: 'monospace', color: '#6b7280' }}>
                        Source session: {pair.source_session} · Channel: {pair.channel}
                    </div>
                )}
            </div>

            {isPending && (
                <div style={{ padding: '10px 16px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 10 }}>
                    <button onClick={() => handle('approve')} disabled={loading}
                        style={{ flex: 1, padding: '8px 0', fontSize: 12, fontWeight: 700, color: '#fff', background: loading ? '#9ca3af' : '#16a34a', border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <i className="bi bi-check-circle" style={{ fontSize: 13 }}></i> Approve → Qdrant
                    </button>
                    <button onClick={() => handle('reject')} disabled={loading}
                        style={{ flex: 1, padding: '8px 0', fontSize: 12, fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <i className="bi bi-x-circle" style={{ fontSize: 13 }}></i> Reject
                    </button>
                </div>
            )}
        </div>
    );
}

export default function LearningReview() {
    const [filter,     setFilter]     = useState('pending');
    const [localPairs, setLocalPairs] = useState(MOCK_PAIRS);
    const [apiPairs,   setApiPairs]   = useState([]);
    const [loading,    setLoading]    = useState(false);

    const fetchPairs = async (status = filter) => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const params = new URLSearchParams({ limit: 20 });
            if (status) params.append('status', status);
            const res = await fetch(`${chatbotUrl}/learning/review?${params}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setApiPairs(data?.data?.items || data?.data || []);
        } catch (e) { setApiPairs([]); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchPairs(); }, []);

    const rawPairs  = apiPairs.length > 0 ? apiPairs : localPairs;
    const displayed = filter ? rawPairs.filter(p => p.status === filter) : rawPairs;
    const pending   = rawPairs.filter(p => p.status === 'pending').length;
    const approved  = rawPairs.filter(p => p.status === 'approved').length;
    const rejected  = rawPairs.filter(p => p.status === 'rejected').length;

    const approve = async (id) => {
        setLocalPairs(prev => prev.map(p => p.id === id ? { ...p, status: 'approved' } : p));
        setApiPairs(prev => prev.map(p => p.id === id ? { ...p, status: 'approved' } : p));
        const token = localStorage.getItem('token');
        try {
            await fetch(`${chatbotUrl}/learning/review/${id}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'approve' }) });
        } catch (e) { /* ignored */ }
        toast.success('Approved — queued for Qdrant upsert', { autoClose: 2000 });
    };

    const reject = async (id) => {
        setLocalPairs(prev => prev.map(p => p.id === id ? { ...p, status: 'rejected' } : p));
        setApiPairs(prev => prev.map(p => p.id === id ? { ...p, status: 'rejected' } : p));
        const token = localStorage.getItem('token');
        try {
            await fetch(`${chatbotUrl}/learning/review/${id}`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'reject' }) });
        } catch (e) { /* ignored */ }
        toast.success('Rejected — pair discarded', { autoClose: 2000 });
    };

    const approveAll = async () => {
        const pendingIds = rawPairs.filter(p => p.status === 'pending').map(p => p.id);
        if (pendingIds.length === 0) { toast('No pending pairs'); return; }
        setLocalPairs(prev => prev.map(p => p.status === 'pending' ? { ...p, status: 'approved' } : p));
        setApiPairs(prev => prev.map(p => p.status === 'pending' ? { ...p, status: 'approved' } : p));
        toast.success(`${pendingIds.length} pairs approved — queued for Qdrant upsert`, { autoClose: 2000 });
    };

    return (
        <WrapperComponent>
            <div style={s.page}>
                <div style={s.pageHeader}>
                    <div>
                        <h5 style={s.pageTitle}>Learning Review Queue</h5>
                        <p style={s.pageSubtitle}>Approve or reject Q&A pairs extracted from WhatsApp session transcripts before they are upserted into Qdrant.</p>
                    </div>
                    {pending > 0 && (
                        <button onClick={approveAll} style={s.btnSuccess}>
                            <i className="bi bi-check-circle" style={{ marginRight: 6 }}></i>Approve All Pending
                        </button>
                    )}
                </div>

                {/* Pipeline */}
                <div style={s.card}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, overflowX: 'auto' }}>
                        {PIPELINE_STEPS.map((step, i) => (
                            <React.Fragment key={step.label}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 10, background: step.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <i className={`bi ${step.icon}`} style={{ fontSize: 18, color: step.color }}></i>
                                    </div>
                                    <p style={{ fontSize: 10, fontWeight: 600, color: '#6b7280', textAlign: 'center', maxWidth: 70, margin: 0 }}>{step.label}</p>
                                </div>
                                {i < PIPELINE_STEPS.length - 1 && <i className="bi bi-chevron-right" style={{ color: '#d1d5db', flexShrink: 0 }}></i>}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Stats */}
                <div style={s.statsRow}>
                    {[
                        { label: 'Pending Review', value: pending,          color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
                        { label: 'Approved',        value: approved,         color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
                        { label: 'Rejected',        value: rejected,         color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
                        { label: 'Total',           value: rawPairs.length,  color: '#1565c0', bg: '#eff6ff', border: '#bfdbfe' },
                    ].map(stat => (
                        <div key={stat.label} style={{ padding: '10px 18px', background: stat.bg, border: `1px solid ${stat.border}`, borderRadius: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span style={{ fontSize: 20, fontWeight: 800, color: stat.color }}>{stat.value}</span>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>{stat.label}</span>
                        </div>
                    ))}
                    <button onClick={() => fetchPairs(filter)} style={s.btnSecondary}>
                        <i className="bi bi-arrow-clockwise" style={{ marginRight: 6 }}></i>Refresh
                    </button>
                </div>

                {/* Filter tabs */}
                <div style={{ display: 'flex', gap: 6 }}>
                    {[
                        { value: 'pending',  label: 'Pending',  count: pending         },
                        { value: 'approved', label: 'Approved', count: approved        },
                        { value: 'rejected', label: 'Rejected', count: rejected        },
                        { value: '',         label: 'All',      count: rawPairs.length },
                    ].map(f => (
                        <button key={f.value} onClick={() => { setFilter(f.value); fetchPairs(f.value); }}
                            style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 20, border: `1.5px solid ${filter === f.value ? '#1565c0' : '#e5e7eb'}`, background: filter === f.value ? '#eff6ff' : '#fff', color: filter === f.value ? '#1565c0' : '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {f.label}
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 10, background: filter === f.value ? '#1565c0' : '#f3f4f6', color: filter === f.value ? '#fff' : '#6b7280' }}>{f.count}</span>
                        </button>
                    ))}
                </div>

                {/* Grid */}
                {loading ? (
                    <div style={s.centerState}>
                        <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                        <p style={s.stateText}>Loading review queue…</p>
                    </div>
                ) : displayed.length === 0 ? (
                    <div style={s.emptyState}>
                        <i className="bi bi-cpu" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                        <p style={{ fontWeight: 600, color: '#374151', margin: 0 }}>No {filter || ''} pairs</p>
                        <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>Q&A pairs are extracted automatically from WhatsApp session transcripts by the Celery worker.</p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 16 }}>
                        {displayed.map(pair => (
                            <QAPairCard key={pair.id} pair={pair} onApprove={approve} onReject={reject}/>
                        ))}
                    </div>
                )}

                <ToastContainer/>
            </div>
        </WrapperComponent>
    );
}

const s = {
    page:        { display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle:   { margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' },
    pageSubtitle:{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' },
    card:        { background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    statsRow:    { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '60px 20px' },
    stateText:   { fontSize: 14, color: '#6b7280', margin: 0 },
    emptyState:  { background: '#fff', border: '2px dashed #e5e7eb', borderRadius: 16, padding: 60, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 },
    btnSuccess:  { display: 'inline-flex', alignItems: 'center', padding: '9px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    btnSecondary:{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
